import {
  InputCheckboxGroup,
  InputCheckbox,
  InputTextField
} from '@abbott-components/core';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import Stack from '@mui/material/Stack';
import ControlState from '../../Shared/controlState';
import { ChangeEvent, useCallback, useContext, useState } from 'react';
import { WorkflowEngineContext } from '../../WorkflowEngine/workflowEngineReducer';
import formatName from '../formatName';
import { useFormContext } from 'react-hook-form';
import RulesEngine from '../Shared/RulesEngine';
import { ConditionalRuleConfig } from '../Shared/RulesEngine/types';
import getDefaultRuleConfig from '../Shared/RulesEngine/getDefaultRuleConfig';
import SizingSelect from '../../Shared/SizingSelect';

export default (control: ControlState) => {
  const { state } = useContext(WorkflowEngineContext);

  const { isReadOnlyView } = state;

  const { schema } = control;

  const properties = schema?.properties as JSONSchema7Properties;

  const decimalPlacesFieldName = formatName(
    properties['decimalPlaces']?.$id as string
  );
  const idFieldName = formatName(properties['id']?.$id as string);
  const labelFieldName = formatName(properties['label']?.$id as string);
  const maximumFieldName = formatName(properties['maximum']?.$id as string);
  const minimumFieldName = formatName(properties['minimum']?.$id as string);
  const requiredFieldName = formatName(properties['required']?.$id as string);
  const rulesFieldName = formatName(properties['rules']?.$id as string);
  const formContext = useFormContext();

  const utilizeHiddenComponentsFieldName = formatName(
    properties['utilizeHiddenComponents']?.$id as string
  );

  const isUtilizeHiddenComponentsEnabled = (): boolean => {
    return (
      (formContext.getValues(utilizeHiddenComponentsFieldName) as boolean) ??
      false
    );
  };

  const [uHCCheckbox, setuHCCheckbox] = useState<boolean>(
    isUtilizeHiddenComponentsEnabled()
  );

  const handleUtilizeHiddenComponentsChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const rules = formContext.getValues(
      rulesFieldName
    ) as ConditionalRuleConfig[];
    setuHCCheckbox(event.target.checked);
    formContext.setValue(
      utilizeHiddenComponentsFieldName,
      event.target.checked
    );
    if (!!event.target.value && !rules.length) {
      updateRules([getDefaultRuleConfig()]);
    } else {
      updateRules([]);
    }
  };

  const updateRules = useCallback((rules: ConditionalRuleConfig[]) => {
    formContext.setValue(rulesFieldName, rules);
  }, []);

  return (
    <Stack direction="column" spacing={3}>
      <Stack direction="row" spacing={3}>
        <Stack sx={{ flexGrow: 4 }}>
          <InputTextField
            name={labelFieldName}
            label="Numeric Label"
            isDisabled={isReadOnlyView}
          />
        </Stack>
        <Stack sx={{ flexGrow: 4 }}>
          <InputTextField
            name={idFieldName}
            label="ID"
            isDisabled={isReadOnlyView}
          />
        </Stack>
        <SizingSelect
          isReadOnlyView={isReadOnlyView}
          name={formatName(properties['gridSize']?.$id as string)}
        />
      </Stack>
      <Stack direction="row" spacing={3}>
        <Stack sx={{ flexGrow: 3 }}>
          <InputTextField
            name={minimumFieldName}
            label="Minimum Value"
            type="number"
            isDisabled={isReadOnlyView}
          />
        </Stack>
        <Stack sx={{ flexGrow: 3 }}>
          <InputTextField
            name={maximumFieldName}
            label="Maximum Value"
            type="number"
            isDisabled={isReadOnlyView}
          />
        </Stack>
        <Stack sx={{ flexGrow: 3 }}>
          <InputTextField
            name={decimalPlacesFieldName}
            label="Decimal Places"
            type="number"
            isDisabled={isReadOnlyView}
          />
        </Stack>
      </Stack>
      <Stack direction="row" spacing={3}>
        <Stack>
          <InputCheckboxGroup
            name={`${requiredFieldName}-inputCheckBoxGroup`}
            isDisabled={isReadOnlyView}
          >
            <InputCheckbox
              name={requiredFieldName}
              defaultChecked={
                (properties['required'].default as boolean) ?? false
              }
            >
              Required Field
            </InputCheckbox>
          </InputCheckboxGroup>
        </Stack>
        <Stack>
          <InputCheckboxGroup
            name={`${utilizeHiddenComponentsFieldName}-inputCheckBoxGroup`}
            isDisabled={isReadOnlyView}
          >
            <InputCheckbox
              defaultChecked={uHCCheckbox}
              name={utilizeHiddenComponentsFieldName}
              onCheckboxChange={handleUtilizeHiddenComponentsChange}
            >
              Utilize Hidden Components
            </InputCheckbox>
          </InputCheckboxGroup>
        </Stack>
      </Stack>
      <Stack>
        {uHCCheckbox && (
          <RulesEngine
            isReadOnlyView={isReadOnlyView}
            fieldName={rulesFieldName}
            updateRules={updateRules}
            fieldId={properties['id']?.default as string}
          />
        )}
        <InputTextField sx={{ display: 'none' }} name={rulesFieldName} />
      </Stack>
    </Stack>
  );
};
